import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Heart, X, Copy, ArrowLeft, Wind } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
/**
 * Dedicated "Brush ritual" page.
 *
 * Flow:
 *   1. /brush/:bubbleId       (initiator)
 *      → POST /brush/start    → shows QR + "TOUCH PHONES" countdown
 *   2. /brush/finish/:token   (recipient, scanned)
 *      → POST /brush/finish   → cinematic split-screen of both bubbles meeting
 *
 * Result: NO ownership change. Both bubbles record having met on the ledger.
 */

const Brush = () => {
  const nav = useNavigate();
  const [step, setStep] = useState('pick'); // pick | broadcast | met
  const [bubbles, setBubbles] = useState([]);
  const [picked, setPicked] = useState(null);
  const [token, setToken] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [met, setMet] = useState(null);

  useEffect(() => {
    api.get('/bubbles/mine').then(({ data }) => setBubbles(data.bubbles || []));
  }, []);

  // Countdown when broadcasting + poll for completion
  useEffect(() => {
    if (step !== 'broadcast' || !token) return;
    const tick = setInterval(async () => {
      setSecondsLeft((s) => Math.max(0, s - 1));
      try {
        const { data } = await api.get(`/brush/peek/${token}`);
        if (data && data.consumed) {} // peek returns 410 if consumed, see catch
      } catch (err) {
        if (err?.response?.status === 410 && err?.response?.data?.detail === 'brush already completed') {
          // Find the brush result by looking at our recent ledger; simpler: just go to bubbles
          clearInterval(tick);
          toast.success('Brush completed elsewhere');
          nav(`/bubble/${picked.id}`);
        }
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [step, token, nav, picked]);

  const startBrush = async () => {
    if (!picked) {
      toast.error('Pick a bubble first');
      return;
    }
    try {
      const { data } = await api.post('/brush/start', { bubble_id: picked.id });
      setToken(data.token);
      setSecondsLeft(data.expires_in_s || 60);
      setStep('broadcast');
      if (navigator.vibrate) try { navigator.vibrate([40, 30, 60]); } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not start brush');
    }
  };

  // The recipient's confirmed-finish state (used when this page is opened
  // with a "result" coming from elsewhere). For the initiator we surface this
  // when their token gets consumed.
  if (step === 'met' && met) return <BrushMet met={met} />;

  return (
    <div className="min-h-screen px-4 py-8 sm:py-12" data-testid="brush-page">
      <button onClick={() => nav(-1)} className="text-white/60 text-sm flex items-center gap-1 hover:text-white">
        <ArrowLeft size={14} /> back
      </button>

      <div className="max-w-2xl mx-auto mt-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-magenta flex items-center justify-center gap-2">
          <Heart size={12} weight="fill" /> brush ritual
        </div>
        <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter mt-2">
          Touch <span className="shimmer-text">without taking</span>
        </h1>
        <p className="text-white/65 mt-4 max-w-lg mx-auto">
          Show this to a friend's phone. Your bubbles meet, exchange a moment, and stay where they are.
          Both bubbles will remember.
        </p>

        {step === 'pick' && (
          <>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {bubbles.map((b) => (
                <button key={b.id} data-testid={`brush-pick-${b.id}`}
                        onClick={() => setPicked(b)}
                        className={`glass rounded-2xl p-3 text-left transition ${
                          picked?.id === b.id ? 'ring-2 ring-bx-magenta' : 'hover:bg-white/5'
                        }`}>
                  {bubbleImageSrc(b) ? (
                    <img src={bubbleImageSrc(b)} alt={b.name}
                         className="h-20 w-20 rounded-full object-cover mx-auto" />
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt mx-auto breathe" />
                  )}
                  <div className="font-display font-bold text-sm mt-2 truncate">{b.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">{b.rarity}</div>
                </button>
              ))}
            </div>
            {bubbles.length === 0 && (
              <div className="mt-10 text-white/60">
                Blow a bubble first.{' '}
                <Link to="/breathe" className="text-bx-cyan font-bold">Breathe one →</Link>
              </div>
            )}
            <button onClick={startBrush} disabled={!picked} data-testid="brush-start"
                    className="btn-primary px-7 py-3.5 rounded-full text-sm inline-flex items-center gap-2 mt-10 disabled:opacity-40">
              <Wind size={14} weight="bold" /> Start brush
            </button>
          </>
        )}

        {step === 'broadcast' && token && (
          <BrushBroadcast token={token} bubble={picked} secondsLeft={secondsLeft}
                          onMet={(m) => { setMet(m); setStep('met'); }} />
        )}
      </div>
    </div>
  );
};

const BrushBroadcast = ({ token, bubble, secondsLeft, onMet }) => {
  const url = `${window.location.origin}/brush/finish/${token}`;
  // While the initiator is waiting, listen on a small "did anyone finish?" loop.
  useEffect(() => {
    let cancelled = false;
    const id = setInterval(async () => {
      // peek returns 410 once consumed; we follow up by checking the ledger
      // for a brush event tied to our bubble.
      if (cancelled) return;
      try {
        await api.get(`/brush/peek/${token}`);
      } catch (err) {
        if (err?.response?.status === 410) {
          clearInterval(id);
          // Fetch the bubble's history to find the brush partner
          const { data } = await api.get('/bubbles/mine');
          const me = (data.bubbles || []).find((b) => b.id === bubble.id);
          const last = (me?.history || []).slice().reverse().find((h) => h.type === 'brush');
          if (last) {
            onMet({
              place: last.place,
              initiator_bubble: { id: bubble.id, name: bubble.name, image_b64: bubble.image_b64 },
              responder_bubble: { id: (last.with || [])[0] },
            });
          } else { onMet({ place: '', initiator_bubble: bubble, responder_bubble: null }); }
        }
      }
    }, 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, bubble, onMet]);

  return (
    <div className="mt-10">
      <div className="text-[10px] uppercase tracking-[0.3em] text-bx-volt mb-2">expires in</div>
      <div className="font-display text-5xl font-black tracking-tighter shimmer-text">{secondsLeft}s</div>

      <div className="relative mx-auto my-8 w-fit">
        <span className="absolute -inset-4 rounded-full border-2 border-bx-magenta animate-ping" />
        <span className="absolute -inset-8 rounded-full border-2 border-bx-cyan animate-ping" style={{ animationDelay: '0.4s' }} />
        <div className="bg-white p-4 rounded-2xl">
          <QRCodeCanvas value={url} size={208} fgColor="#050505" bgColor="#ffffff"
                        data-testid="brush-qr" />
        </div>
      </div>

      <div className="text-sm text-white/65 mb-3">
        Have your friend scan this QR. No ownership changes. Both bubbles will remember meeting.
      </div>

      <button onClick={() => { navigator.clipboard.writeText(url); toast.success('Brush link copied'); }}
              className="text-bx-cyan font-bold text-sm inline-flex items-center gap-1.5"
              data-testid="brush-copy-link">
        <Copy size={14} weight="bold" /> copy brush link
      </button>
    </div>
  );
};

const BrushMet = ({ met }) => {
  return (
    <div className="min-h-screen grid place-items-center px-4 py-10" data-testid="brush-met">
      <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 140 }}
                  className="max-w-lg w-full text-center">
        <Heart size={48} weight="fill" className="text-bx-magenta mx-auto" />
        <h1 className="font-display text-5xl font-black tracking-tighter mt-4">
          They <span className="shimmer-text">met</span>
        </h1>
        <p className="text-white/65 mt-3">
          Both bubbles now carry the memory of this moment{met.place ? ` at ${met.place}` : ''}.
        </p>

        <div className="my-10 flex items-center justify-center gap-6">
          <BubbleAvatar b={met.initiator_bubble} />
          <motion.div animate={{ scale: [1, 1.3, 1], rotate: [0, 12, -12, 0] }}
                      transition={{ duration: 1.4, repeat: Infinity }}>
            <Heart size={32} weight="fill" className="text-bx-magenta" />
          </motion.div>
          <BubbleAvatar b={met.responder_bubble} />
        </div>

        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan">
          recorded on the public ledger · Tier C · story
        </div>
      </motion.div>
    </div>
  );
};

const BubbleAvatar = ({ b }) => (
  <div className="text-center">
    {bubbleImageSrc(b) ? (
      <img src={bubbleImageSrc(b)} alt={b.name}
           className="h-24 w-24 rounded-full object-cover ring-2 ring-bx-magenta/40 breathe" />
    ) : (
      <div className="h-24 w-24 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe" />
    )}
    <div className="font-display font-bold text-sm mt-2 truncate max-w-[6rem]">
      {b?.name || '…'}
    </div>
  </div>
);

export default Brush;
